// @flow
import React, { Component, type Node } from 'react';
import {
  ModelListTable,
  ModelListTh,
  ModelListDiv
} from '../../../../components/ModelListStyles';
import {
  Default,
  LgDesktop,
  Desktop,
  Device
} from '../../../../components/Responsive';
import Paginator from '../../../../components/Paginator';
import EmptyTable from '../../../../components/EmptyTable';
import TableLoader from '../../../../components/TableLoader';
import DirectMerchantRow from './DirectMerchantRow';
import CustomPerPageFilter from '../../../../modules/CrudTable/components/CustomPerPageFilter';
import UpDownSwitch from '../../../../components/UpDownSwitch';
import InfoTooltip from '../../../../components/InfoTooltip';
import { type Currencies, type Merchant } from '../../../../utilities/types';
import { SortableSwitchDiv } from './DirectMerchantsIndexStyles';
import DefaultModelRowTr from '../../../../modules/CrudTable/components/DefaultModelRowTr';

type Props = {
  directMerchants: Array<Merchant>,
  changeSortOrder: (orderBy: string) => void,
  showTooltip: (tooltip: string) => void,
  activeTooltip: ?string,
  orderBy: ?string,
  orderDirection: ?string,
  currentPage?: number,
  previouslyVisited?: number[],
  changePage?: (value: number) => void,
  perPageOptions?: Array<number>,
  paginatorTotal?: number,
  currentPerPage?: string,
  currencies: Currencies,
  setPerPage?: (perPage: number) => void,
  showMostRecentOnly?: boolean,
  setActiveSubsection: (subsection: number) => void,
  loading: boolean,
  failedLoad: boolean
};

export default class DirectMerchantsIndexTable extends Component<Props> {
  renderBody = () => {
    if (this.props.loading) {
      return <TableLoader columns={6} />;
    } else if (this.props.failedLoad) {
      return <EmptyTable error model="Direct Merchants" columns={6} />;
    } else if (this.props.directMerchants.length === 0) {
      return (
        <EmptyTable
          text="No direct merchants to show"
          columns={6}
          model="Direct Merchants"
          img="merchants"
        />
      );
    } else {
      return this.props.directMerchants.map<Node>(directMerchant => (
        <React.Fragment key={directMerchant.id}>
          <DirectMerchantRow
            key={directMerchant.id}
            merchant={directMerchant}
            showTooltip={this.props.showTooltip}
            activeTooltip={this.props.activeTooltip}
            currencies={this.props.currencies}
            showMostRecentOnly={this.props.showMostRecentOnly}
            setActiveSubsection={this.props.setActiveSubsection}
          />
        </React.Fragment>
      ));
    }
  };
  render() {
    return (
      <ModelListDiv withPagination>
        <ModelListTable smallMarginBottom>
          <thead>
            <DefaultModelRowTr>
              <LgDesktop>
                <ModelListTh>ID #</ModelListTh>
                <ModelListTh>Date</ModelListTh>
              </LgDesktop>
              <ModelListTh>
                <Desktop>Direct Merchant Name</Desktop>
                <Device>Direct Merchants</Device>
              </ModelListTh>
              <Default>
                <ModelListTh>Status</ModelListTh>
              </Default>
              <ModelListTh right clickable>
                <InfoTooltip
                  showTooltip={this.props.showTooltip}
                  activeTooltip={this.props.activeTooltip}
                  tooltipName={'DIRECT_MERCHANTS_PROCESSING_VOLUME'}
                  message="The amounts below are estimates based on the exchange rate at the time of purchase. Click to view exact breakdowns / details"
                  smallIcon
                />{' '}
                {this.props.showMostRecentOnly ? (
                  'Processing Vol'
                ) : (
                  <SortableSwitchDiv
                    onClick={() =>
                      this.props.changeSortOrder('total_processing_vol_usd')
                    }
                  >
                    Processing Vol.
                    <UpDownSwitch
                      upFillColor={
                        this.props.orderBy === 'total_processing_vol_usd' &&
                        this.props.orderDirection === 'asc'
                          ? 'dustierGray'
                          : 'black'
                      }
                      downFillColor={
                        this.props.orderBy === 'total_processing_vol_usd' &&
                        this.props.orderDirection === 'desc'
                          ? 'dustierGray'
                          : 'black'
                      }
                    />
                  </SortableSwitchDiv>
                )}
              </ModelListTh>
              <Desktop>
                <ModelListTh right clickable>
                  <InfoTooltip
                    showTooltip={this.props.showTooltip}
                    activeTooltip={this.props.activeTooltip}
                    tooltipName={'DIRECT_MERCHANTS_TRANSACTION_FEES'}
                    message="The amounts below are estimates based on the exchange rate at the time of purchase. Click to view exact breakdowns / details"
                    smallIcon
                  />{' '}
                  {this.props.showMostRecentOnly ? (
                    'Trans.Fees'
                  ) : (
                    <SortableSwitchDiv
                      onClick={() =>
                        this.props.changeSortOrder('total_fees_usd')
                      }
                    >
                      Trans. Fees
                      <UpDownSwitch
                        upFillColor={
                          this.props.orderBy === 'total_fees_usd' &&
                          this.props.orderDirection === 'asc'
                            ? 'dustierGray'
                            : 'black'
                        }
                        downFillColor={
                          this.props.orderBy === 'total_fees_usd' &&
                          this.props.orderDirection === 'desc'
                            ? 'dustierGray'
                            : 'black'
                        }
                      />
                    </SortableSwitchDiv>
                  )}
                </ModelListTh>
              </Desktop>
            </DefaultModelRowTr>
          </thead>
          <tbody>{this.renderBody()}</tbody>
        </ModelListTable>
        {Boolean(!this.props.loading) &&
          Boolean(this.props.perPageOptions) &&
          this.props.directMerchants.length > 0 && (
            <CustomPerPageFilter
              currentPerPage={this.props.currentPerPage || ''}
              // $FlowFixMe
              setPerPage={this.props.setPerPage}
              perPageOptions={this.props.perPageOptions || []}
              alignWithTable
            />
          )}
        {Boolean(!this.props.loading) &&
          Boolean(this.props.paginatorTotal) &&
          this.props.directMerchants.length > 0 && (
            // $FlowFixMe
            <Paginator
              paginatorTotal={this.props.paginatorTotal}
              currentPage={this.props.currentPage}
              changePage={this.props.changePage}
              previouslyVisited={this.props.previouslyVisited}
            />
          )}
      </ModelListDiv>
    );
  }
}
